<template>
  <Head title="Jabatan" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="min-h-screen bg-gray-50">
        <div class="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <Breadcrumbs
            :breadcrumbs="[
              { name: 'Dashboard', href: route('dashboard') },
              { name: 'Jabatan', href: route('jabatan.index') },
            ]"
          />

          <!-- Header Section -->
          <div class="mb-8">
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <h1 class="text-3xl font-bold text-gray-900">
                  Manajemen Jabatan
                </h1>
                <p class="mt-2 text-lg text-gray-600">
                  Kelola data jabatan dalam sistem
                </p>
              </div>
            </div>
          </div>

          <!-- Form Input Section -->
          <div
            class="mb-8 bg-white border border-gray-200 shadow-lg rounded-xl"
          >
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-blue-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h2
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-plus-circle sm:mr-3"></i>
                {{ form.id ? "Edit Jabatan" : "Tambah Jabatan Baru" }}
              </h2>
            </div>

            <div class="p-4 sm:p-6">
              <form @submit.prevent="submitForm" class="space-y-4">
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  <div class="lg:col-span-2">
                    <label
                      for="namajabatan"
                      class="block mb-2 text-sm font-medium text-gray-700"
                    >
                      <i class="mr-2 text-gray-500 fa-solid fa-briefcase"></i>
                      Nama Jabatan
                    </label>
                    <input
                      id="namajabatan"
                      v-model="form.namajabatan"
                      type="text"
                      required
                      placeholder="Masukkan nama jabatan..."
                      class="w-full px-4 py-3 text-sm transition-colors duration-200 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      :class="{
                        'border-red-500 focus:ring-red-500 focus:border-red-500':
                          form.errors.namajabatan,
                      }"
                    />
                    <div
                      v-if="form.errors.namajabatan"
                      class="mt-1 text-sm text-red-600"
                    >
                      <i class="mr-1 fa-solid fa-exclamation-circle"></i>
                      {{ form.errors.namajabatan }}
                    </div>
                  </div>

                  <div class="flex items-end">
                    <div class="flex w-full space-x-2">
                      <button
                        type="submit"
                        :disabled="form.processing"
                        class="inline-flex items-center justify-center flex-1 px-4 py-3 text-sm font-semibold text-white transition-colors duration-200 bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i
                          v-if="form.processing"
                          class="mr-2 fa-solid fa-spinner fa-spin"
                        ></i>
                        <i v-else class="mr-2 fa-solid fa-save"></i>
                        <span class="hidden sm:inline">
                          {{
                            form.processing
                              ? "Menyimpan..."
                              : form.id
                              ? "Update"
                              : "Simpan"
                          }}
                        </span>
                        <span class="sm:hidden">
                          {{ form.processing ? "..." : form.id ? "Edit" : "+" }}
                        </span>
                      </button>

                      <button
                        v-if="form.id"
                        type="button"
                        @click="cancelEdit"
                        class="px-4 py-3 text-sm font-medium text-gray-700 transition-colors duration-200 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                      >
                        <i class="mr-1 fa-solid fa-times"></i>
                        <span class="hidden sm:inline">Batal</span>
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <!-- Main Content Card -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-briefcase sm:mr-3"></i>
                  Data Jabatan
                </h2>

                <!-- Search Input -->
                <div class="relative w-full mt-3 sm:mt-0 sm:w-80">
                  <div
                    class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
                  >
                    <svg
                      class="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      ></path>
                    </svg>
                  </div>
                  <input
                    v-model="search"
                    type="text"
                    autocomplete="off"
                    placeholder="Cari jabatan..."
                    class="w-full py-2 pl-10 pr-4 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-white focus:border-white placeholder:text-gray-400"
                  />
                </div>
              </div>
            </div>

            <div class="p-4 sm:p-6">
              <!-- Bulk action bar -->
              <div
                v-if="selectedIds.length > 0"
                class="flex flex-col gap-3 p-3 mb-4 border border-red-200 rounded-lg sm:flex-row sm:items-center sm:justify-between bg-red-50"
              >
                <span class="text-sm font-medium text-red-800">
                  <i class="mr-1 fa-solid fa-circle-check"></i>
                  {{ selectedIds.length }} dari {{ jabatan.data?.length || 0 }} item dipilih
                </span>
                <div class="flex items-center gap-2">
                  <button
                    @click="bulkDelete"
                    :disabled="bulkDeleteForm.processing"
                    class="inline-flex items-center px-3 py-2 text-sm font-semibold text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i
                      class="w-4 h-4 mr-2 fa-solid fa-trash"
                      :class="{ 'fa-spin': bulkDeleteForm.processing }"
                    ></i>
                    {{ bulkDeleteForm.processing ? "Menghapus..." : "Hapus Terpilih" }}
                  </button>
                  <button
                    @click="clearSelection"
                    :disabled="bulkDeleteForm.processing"
                    class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Batal
                  </button>
                </div>
              </div>

              <!-- Desktop Table -->
              <div
                class="hidden overflow-hidden border border-gray-200 rounded-lg lg:block"
              >
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th scope="col" class="w-12 px-6 py-3 text-left">
                        <input
                          type="checkbox"
                          :checked="isAllSelected"
                          :indeterminate.prop="isIndeterminate"
                          @change="toggleSelectAll"
                          class="w-4 h-4 text-indigo-600 border-gray-300 rounded cursor-pointer focus:ring-indigo-500"
                          title="Pilih semua"
                        />
                      </th>
                      <th
                        scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                      >
                        <div class="flex items-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-hashtag"></i>
                          <span>No</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                      >
                        <div class="flex items-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-briefcase"></i>
                          <span>Nama Jabatan</span>
                        </div>
                      </th>
                      <th
                        scope="col"
                        class="px-6 py-3 text-xs font-medium tracking-wider text-center text-gray-500 uppercase"
                      >
                        <div class="flex items-center justify-center space-x-1">
                          <i class="text-gray-400 fa-solid fa-cogs"></i>
                          <span>Aksi</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-if="!jabatan.data || jabatan.data.length === 0">
                      <td
                        colspan="4"
                        class="px-6 py-8 text-center text-gray-500"
                      >
                        <div class="flex flex-col items-center">
                          <i
                            class="mb-3 text-4xl text-gray-300 fa-solid fa-briefcase"
                          ></i>
                          <p class="text-lg font-medium">
                            Belum ada data jabatan
                          </p>
                          <p class="text-sm">
                            Gunakan form di atas untuk menambah data baru
                          </p>
                        </div>
                      </td>
                    </tr>
                    <tr
                      v-for="(jabat, index) in jabatan.data"
                      :key="jabat.id"
                      class="transition-colors duration-150"
                      :class="
                        selectedIds.includes(jabat.id)
                          ? 'bg-indigo-50'
                          : 'hover:bg-gray-50'
                      "
                    >
                      <td class="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          :value="jabat.id"
                          v-model="selectedIds"
                          class="w-4 h-4 text-indigo-600 border-gray-300 rounded cursor-pointer focus:ring-indigo-500"
                        />
                      </td>
                      <td
                        class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap"
                      >
                        {{ index + 1 }}
                      </td>
                      <td class="px-6 py-4 whitespace-nowrap">
                        <div class="flex items-center">
                          <div class="flex-shrink-0">
                            <div
                              class="flex items-center justify-center w-8 h-8 bg-indigo-100 rounded-full"
                            >
                              <i
                                class="text-sm text-indigo-600 fa-solid fa-briefcase"
                              ></i>
                            </div>
                          </div>
                          <div class="ml-3">
                            <div class="text-sm font-medium text-gray-900">
                              {{ jabat.namajabatan }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="px-6 py-4 text-center whitespace-nowrap">
                        <div class="flex items-center justify-center space-x-2">
                          <button
                            @click="editJabatan(jabat)"
                            class="inline-flex items-center px-3 py-2 text-xs font-medium text-indigo-700 transition-colors duration-200 bg-indigo-100 border border-transparent rounded-lg hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                            title="Edit Jabatan"
                          >
                            <i class="mr-1 fa-solid fa-edit"></i>
                            Edit
                          </button>
                          <button
                            @click="deleteJabatan(jabat.id)"
                            class="inline-flex items-center px-3 py-2 text-xs font-medium text-red-700 transition-colors duration-200 bg-red-100 border border-transparent rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            title="Hapus Jabatan"
                          >
                            <i class="mr-1 fa-solid fa-trash"></i>
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile Cards -->
              <div class="space-y-4 lg:hidden">
                <!-- Mobile select all -->
                <div
                  v-if="jabatan.data && jabatan.data.length > 0"
                  class="flex items-center justify-between px-1"
                >
                  <label class="inline-flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      :checked="isAllSelected"
                      :indeterminate.prop="isIndeterminate"
                      @change="toggleSelectAll"
                      class="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    Pilih semua
                  </label>
                  <span v-if="selectedIds.length" class="text-xs text-gray-500">
                    {{ selectedIds.length }} dipilih
                  </span>
                </div>

                <div
                  v-if="!jabatan.data || jabatan.data.length === 0"
                  class="py-8 text-center text-gray-500"
                >
                  <div class="flex flex-col items-center">
                    <i
                      class="mb-3 text-4xl text-gray-300 fa-solid fa-briefcase"
                    ></i>
                    <p class="text-lg font-medium">Belum ada data jabatan</p>
                    <p class="text-sm">
                      Gunakan form di atas untuk menambah data baru
                    </p>
                  </div>
                </div>

                <div
                  v-for="(jabat, index) in jabatan.data"
                  :key="jabat.id"
                  class="transition-shadow duration-200 bg-white border rounded-lg shadow-sm hover:shadow-md"
                  :class="
                    selectedIds.includes(jabat.id)
                      ? 'border-indigo-300 ring-1 ring-indigo-200'
                      : 'border-gray-200'
                  "
                >
                  <div class="p-4">
                    <div class="flex items-start justify-between">
                      <div class="flex items-center flex-1">
                        <input
                          type="checkbox"
                          :value="jabat.id"
                          v-model="selectedIds"
                          class="w-4 h-4 mr-3 text-indigo-600 border-gray-300 rounded cursor-pointer shrink-0 focus:ring-indigo-500"
                        />
                        <div
                          class="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-indigo-100 rounded-full"
                        >
                          <i class="text-indigo-600 fa-solid fa-briefcase"></i>
                        </div>
                        <div class="flex-1 min-w-0 ml-3">
                          <div class="flex items-center space-x-2">
                            <span
                              class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full"
                            >
                              #{{ index + 1 }}
                            </span>
                          </div>
                          <h3
                            class="mt-1 text-sm font-medium text-gray-900 truncate"
                          >
                            {{ jabat.namajabatan }}
                          </h3>
                        </div>
                      </div>
                    </div>

                    <!-- Mobile Action Buttons -->
                    <div class="flex mt-4 space-x-2">
                      <button
                        @click="editJabatan(jabat)"
                        class="inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-medium text-indigo-700 transition-colors duration-200 border border-indigo-200 rounded-lg bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <i class="mr-1 fa-solid fa-edit"></i>
                        Edit
                      </button>
                      <button
                        @click="deleteJabatan(jabat.id)"
                        class="inline-flex items-center justify-center flex-1 px-3 py-2 text-sm font-medium text-red-700 transition-colors duration-200 border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <i class="mr-1 fa-solid fa-trash"></i>
                        Hapus
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div class="mt-6" v-if="jabatan.data && jabatan.data.length > 0">
                <!-- Desktop Pagination -->
                <div class="hidden sm:flex sm:justify-center">
                  <Pagination
                    :data="jabatan"
                    :updatedPageNumber="updatedPageNumber"
                  />
                </div>

                <!-- Mobile Pagination -->
                <div class="sm:hidden">
                  <div class="flex items-center justify-between px-2">
                    <!-- Previous Button -->
                    <button
                      @click="
                        updatedPageNumber(
                          jabatan.prev_page_url
                            ? { url: jabatan.prev_page_url }
                            : null
                        )
                      "
                      :disabled="!jabatan.prev_page_url"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i class="mr-1 fa-solid fa-chevron-left"></i>
                      Prev
                    </button>

                    <!-- Page Info -->
                    <div class="flex items-center space-x-2">
                      <span class="text-sm text-gray-700">
                        Hal {{ jabatan.current_page }} dari
                        {{ jabatan.last_page }}
                      </span>
                    </div>

                    <!-- Next Button -->
                    <button
                      @click="
                        updatedPageNumber(
                          jabatan.next_page_url
                            ? { url: jabatan.next_page_url }
                            : null
                        )
                      "
                      :disabled="!jabatan.next_page_url"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <i class="ml-1 fa-solid fa-chevron-right"></i>
                    </button>
                  </div>

                  <!-- Mobile Page Numbers (compact) -->
                  <div
                    class="flex justify-center mt-3"
                    v-if="jabatan.last_page > 1"
                  >
                    <div class="flex items-center space-x-1">
                      <button
                        v-for="page in getMobilePageNumbers()"
                        :key="page"
                        @click="
                          updatedPageNumber({
                            url: `${route('jabatan.index')}?page=${page}`,
                          })
                        "
                        :class="[
                          'w-8 h-8 text-xs font-medium rounded-full transition-colors duration-200',
                          page === jabatan.current_page
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
                        ]"
                      >
                        {{ page }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { useForm, usePage, router } from "@inertiajs/vue3";
import { ref, computed, watch, inject } from "vue";
import Swal from "sweetalert2";

import Pagination from "@/Components/Pagination.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
const route = inject('route');

const props = defineProps({
  jabatan: {
    type: Object,
    required: true,
  },
});

// Form untuk input jabatan
const form = useForm({
  id: null,
  namajabatan: "",
});

// ===== Bulk select & delete =====
const selectedIds = ref([]);
const bulkDeleteForm = useForm({ ids: [] });

const currentIds = computed(() =>
  (props.jabatan.data || []).map((j) => j.id)
);
const isAllSelected = computed(
  () =>
    currentIds.value.length > 0 &&
    currentIds.value.every((id) => selectedIds.value.includes(id))
);
const isIndeterminate = computed(
  () => selectedIds.value.length > 0 && !isAllSelected.value
);

const toggleSelectAll = () => {
  if (isAllSelected.value) {
    selectedIds.value = [];
  } else {
    selectedIds.value = [...currentIds.value];
  }
};

const clearSelection = () => {
  selectedIds.value = [];
};

const bulkDelete = async () => {
  if (selectedIds.value.length === 0) return;
  const result = await Swal.fire({
    title: "Konfirmasi Hapus",
    text: `${selectedIds.value.length} jabatan terpilih akan dihapus permanen!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  });
  if (!result.isConfirmed) return;

  bulkDeleteForm.ids = selectedIds.value;
  bulkDeleteForm.post(route("jabatan.bulk-delete"), {
    preserveScroll: true,
    onSuccess: () => {
      clearSelection();
    },
    onError: () => {
      Swal.fire({
        icon: "error",
        title: "Gagal Menghapus!",
        text: "Terjadi kesalahan saat menghapus jabatan terpilih.",
        confirmButtonText: "OK",
        confirmButtonColor: "#dc3545",
      });
    },
  });
};

let search = ref(usePage().props.search || "");
let pageNumber = ref(1);

let studentUrl = computed(() => {
  let url = new URL(route("jabatan.index"));
  url.searchParams.set("page", pageNumber.value);
  if (search.value) {
    url.searchParams.set("search", search.value);
  }
  return url;
});

const updatedPageNumber = (link) => {
  const params = new URLSearchParams(link.url.split("?")[1]);
  pageNumber.value = params.get("page");
};

watch(
  () => studentUrl.value,
  (updatedStudentUrl) => {
    router.visit(updatedStudentUrl.toString(), {
      preserveScroll: true,
      preserveState: true,
      replace: true,
    });
  }
);

watch(
  () => search.value,
  (value) => {
    if (value) {
      pageNumber.value = 1;
    }
    clearSelection();
  }
);

// Submit form untuk tambah/edit jabatan
const submitForm = () => {
  if (form.id) {
    // Update existing jabatan
    form.put(route("jabatan.update", form.id), {
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: "Berhasil Diperbarui!",
          text: `Jabatan "${form.namajabatan}" berhasil diperbarui.`,
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
          timerProgressBar: true,
          showCloseButton: true,
        });
        form.reset();
      },
      onError: (errors) => {
        Swal.fire({
          icon: "error",
          title: "Gagal Memperbarui!",
          text: "Terjadi kesalahan saat memperbarui jabatan. Silakan coba lagi.",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      },
    });
  } else {
    // Create new jabatan
    form.post(route("jabatan.store"), {
      onSuccess: () => {
        form.reset();
      },
      onError: (errors) => {
        Swal.fire({
          icon: "error",
          title: "Gagal Menambahkan!",
          text: "Terjadi kesalahan saat menambahkan jabatan. Silakan periksa data dan coba lagi.",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      },
    });
  }
};

// Cancel edit mode
const cancelEdit = () => {
  form.reset();
  form.clearErrors();
};

const deleteJabatan = async (id) => {
  const result = await Swal.fire({
    title: "Konfirmasi Hapus",
    text: "Apakah Anda yakin ingin menghapus jabatan ini?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  });

  if (result.isConfirmed) {
    const deleteForm = useForm({});
    deleteForm.delete(route("jabatan.destroy", id), {
      onSuccess: () => {},
      onError: (errors) => {
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus!",
          text: "Terjadi kesalahan saat menghapus jabatan. Silakan coba lagi.",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545",
        });
      },
    });
  }
};

const editJabatan = (jabat) => {
  form.id = jabat.id;
  form.namajabatan = jabat.namajabatan;
  // Scroll to form
  document
    .querySelector("form")
    .scrollIntoView({ behavior: "smooth", block: "center" });
};

// Function to get mobile pagination numbers (max 5 pages)
const getMobilePageNumbers = () => {
  const current = jabatan.current_page;
  const last = jabatan.last_page;
  const pages = [];

  if (last <= 5) {
    // Show all pages if total pages <= 5
    for (let i = 1; i <= last; i++) {
      pages.push(i);
    }
  } else {
    // Show smart pagination
    if (current <= 3) {
      // Show first 5 pages
      for (let i = 1; i <= 5; i++) {
        pages.push(i);
      }
    } else if (current >= last - 2) {
      // Show last 5 pages
      for (let i = last - 4; i <= last; i++) {
        pages.push(i);
      }
    } else {
      // Show current page with 2 pages on each side
      for (let i = current - 2; i <= current + 2; i++) {
        pages.push(i);
      }
    }
  }

  return pages;
};
</script>
